/* storybook-check-ignore */
import styled from '@emotion/styled';
import { Flex, Text, Link } from '@opendoor/bricks/core';
import { ListingWithComputedProperties } from 'declarations/exclusives/listing';
import { motion, AnimatePresence } from 'framer-motion';
import { getIsSellerOfferExperiment, getListingState } from 'helpers/exclusives/listing';
import Benefit from './Benefit';
import { ListingStates } from './constants/constants';
import DiscountIcon from './icons/DiscountIcon';
import LetterIcon from './icons/LetterIcon';
import SparkleIcon from './icons/SparkleIcon';
import ArrowIcon from './icons/ArrowIcon';
import SelfTourIcon from './icons/SelfTourIcon';
import BackOutIcon from './icons/BackOutIcon';

const HorizontalLine = styled(motion.hr)`
  width: 100%;
  opacity: 0.3;
  border: 1px solid #cccccc;
  margin: 12px 0;
`;

function CustomerOwnedBenefits({ listing }: { listing: ListingWithComputedProperties }) {
  const isAvailableSoon = getListingState(listing) === ListingStates.EL_SETUP;
  return (
    <>
      <Benefit
        Icon={() => <SparkleIcon size={16} />}
        title="Only on Opendoor Exclusives for a limited time"
      >
        {isAvailableSoon &&
          'This is an off market home for sale that will only be only on Exclusives for a limited time- you won’t find it listed anywhere else.'}
        {!isAvailableSoon &&
          `${listing.exclusive_listing_info.display_street_name} is an off-market home for sale
          only on Exclusives for a limited time. You won’t find it listed anywhere else. The
          home will be available until the seller accepts an offer, or decides to remove the
          listing.`}
      </Benefit>
      <HorizontalLine />
      <Benefit Icon={() => <DiscountIcon size={16} />} title="Exclusive Savings">
        When you buy with Exclusives you pay less. Since we represent the seller, we can use our
        commissions to pass savings back.
      </Benefit>
      <HorizontalLine />
      <Benefit Icon={() => <LetterIcon size={16} />} title="Appraisal Price Match Guarantee">
        We want you to feel confident about your purchase. If the home appraises lower than the
        price you agree with the seller, we’ll match the appraised price up to $50,000.
        <Link
          display="block"
          target="_blank"
          href={`/exclusives/faq?toExpand=how-does-the-appraisal-price-match-guarantee-apply-to-customer-owned-exclusives`}
          aria-label="Appraisal Price Match Guarantee"
          analyticsName="cosmos-exclusives-gallery-sidebar-appraisal-faq-exclusive-price"
        >
          Learn more
        </Link>
      </Benefit>
    </>
  );
}

function OpendoorOwnedBenefits({ listing }: { listing: ListingWithComputedProperties }) {
  const isAvailableSoon = getListingState(listing) === ListingStates.EL_SETUP;
  return (
    <>
      <Benefit
        Icon={() => <SparkleIcon size={16} />}
        title={isAvailableSoon ? 'Only on Opendoor Exclusives' : 'Only Exclusive for 14 days'}
      >
        {isAvailableSoon &&
          'This is an off market home for sale that will only be only on Exclusives for a limited time you won’t find it listed anywhere else.'}
        {!isAvailableSoon &&
          `${listing.exclusive_listing_info.display_street_name} is an off market home for sale only on Exclusives for a limited time you won’t find it listed anywhere else. When the timer runs out, this home will be publicly listed at a higher price.`}
      </Benefit>
      <HorizontalLine />
      <Benefit
        Icon={() => <ArrowIcon size={16} />}
        title="Buy instantly on Exclusives - no bids or offers"
      >
        {isAvailableSoon &&
          'Once it’s available, the first person to sign the contract will be able to purchase the home at its Exclusive Price. You don’t need to agonize about losing out to a higher bid. '}
        {!isAvailableSoon &&
          'The first person to sign the contract will be able to purchase the home at its current Exclusive Price. You don’t need to agonize about losing out to a higher bid. '}
      </Benefit>
      <HorizontalLine />
      <Benefit Icon={() => <DiscountIcon size={16} />} title="Exclusive Price">
        We can save on traditional fees when we can sell a home directly to customers, those savings
        are reflected in the Exclusive Price.
      </Benefit>
      <HorizontalLine />
      <Benefit Icon={() => <SelfTourIcon size={16} />} title="Self tour everyday from 8AM - 8PM">
        {isAvailableSoon &&
          'Once it’s available, this home will be open for self-tours 8am-8pm local time any day of the week. You’ll be able to visit as often as you like to get comfortable with the home.'}
        {!isAvailableSoon &&
          'This home is open for self-tours 8am-8pm local time any day of the week.  You’ll be able to visit as often as you like to get comfortable with the home.'}
      </Benefit>
      <HorizontalLine />
      <Benefit Icon={() => <LetterIcon size={16} />} title="Appraisal Price Match Guarantee">
        If the home appraises at a lower price, we'll match the appraised price up to $50,000.
        <Link
          display="block"
          target="_blank"
          href={`/exclusives/faq?apmg=1`}
          aria-label="Appraisal Price Match Guarantee"
          analyticsName="cosmos-exclusives-gallery-sidebar-appraisal-faq-exclusive-price"
        >
          Learn more
        </Link>
      </Benefit>
      <HorizontalLine />
      <Benefit Icon={() => <BackOutIcon size={16} />} title="Back out anytime">
        If you change your mind at any point in the buying process before closing, you can back out
        with zero penalties. No questions asked. You’ll get 100% of your earnest money deposit back.
        We want you to feel great about the purchase of your next home.
      </Benefit>
    </>
  );
}

export default function Benefits({ listing }: { listing: ListingWithComputedProperties }) {
  const isSellerOfferExperiment = getIsSellerOfferExperiment(listing);
  return (
    <Flex flexDirection="column" as="section" mt="3rem">
      <Text fontSize={'18px' as any} mb="1.375rem" fontWeight="semibold" as="h2">
        Exclusive benefits for this home
      </Text>
      <AnimatePresence presenceAffectsLayout exitBeforeEnter>
        {isSellerOfferExperiment && <CustomerOwnedBenefits listing={listing} />}
        {!isSellerOfferExperiment && <OpendoorOwnedBenefits listing={listing} />}
      </AnimatePresence>
    </Flex>
  );
}
